import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import RoutingExplainer from "@/components/dashboard/RoutingExplainer";
import DeliveryHoursCard from "@/components/dashboard/DeliveryHoursCard";
import CriteriaBuilder from "@/components/dashboard/CriteriaBuilder";
import { Megaphone, Wallet, PauseCircle, PlayCircle, Loader2, ArrowUpRight, Gauge, Activity, CalendarClock, Inbox, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * /dashboard/campaign — the single page a buyer looks at every morning to know
 * "is my machine running?" Shows every campaign owned by this buyer with
 * sector, live status, MTD delivery, today's count, business hours, daily
 * cap, pause toggle, wallet balance, and the routing explainer.
 *
 * Brand: navy/slate, sharp edges, no traffic-light colours. Subtle.
 */
export default function ClientCampaign() {
  const [data, setData] = useState(null);
  const [capDraft, setCapDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const [pausing, setPausing] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/client/campaigns");
      setData(r.data);
      setCapDraft(r.data?.daily_lead_cap ?? "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load campaign view");
    }
  };
  useEffect(() => { refresh(); }, []);

  const togglePause = async () => {
    if (!data) return;
    setPausing(true);
    try {
      const r = await api.post("/client/pause-delivery", { paused: !data.delivery_paused });
      setData((d) => ({ ...d, delivery_paused: r.data.delivery_paused }));
      toast.success(r.data.delivery_paused ? "Delivery paused across all campaigns." : "Delivery resumed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle delivery");
    } finally {
      setPausing(false);
    }
  };

  const saveCap = async () => {
    setSavingCap(true);
    try {
      const cap = capDraft === "" || capDraft === null ? 0 : Math.max(0, parseInt(capDraft, 10) || 0);
      await api.patch("/client/preferences", { daily_lead_cap: cap });
      toast.success(cap === 0 ? "Daily cap removed — leads will flow unlimited." : `Daily cap set to ${cap.toLocaleString()} leads.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save cap");
    } finally {
      setSavingCap(false);
    }
  };

  if (!data) {
    return (
      <div className="space-y-6">
        <SectionTitle title="Campaign" subtitle="Loading your campaign view…" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const { campaigns, wallet_balance, delivery_paused, daily_lead_cap, delivery_hours } = data;
  const liveCamps = campaigns.filter((c) => c.live);
  const totalToday = campaigns.reduce((s, c) => s + (c.today_accepted || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionTitle
          title="Campaign"
          subtitle="Your live campaigns, business hours, caps, and delivery state — all in one place."
        />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild className="border-slate-200">
            <Link to="/dashboard/billing" data-testid="campaign-topup">
              <Wallet className="size-3.5 mr-1.5" /> Top up wallet
            </Link>
          </Button>
          <Button
            size="sm"
            variant={delivery_paused ? "default" : "outline"}
            className={delivery_paused ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
            onClick={togglePause}
            disabled={pausing}
            data-testid="campaign-pause-toggle"
          >
            {pausing
              ? <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              : delivery_paused
                ? <PlayCircle className="size-3.5 mr-1.5" />
                : <PauseCircle className="size-3.5 mr-1.5" />}
            {delivery_paused ? "Resume delivery" : "Pause all delivery"}
          </Button>
        </div>
      </div>

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          Icon={Megaphone}
          label="Live campaigns"
          value={liveCamps.length}
          sub={`of ${campaigns.length} total`}
          testid="kpi-live-campaigns"
        />
        <KpiCard
          Icon={Inbox}
          label="Accepted today"
          value={totalToday.toLocaleString()}
          sub={daily_lead_cap ? `cap ${daily_lead_cap.toLocaleString()}/day` : "no cap"}
          testid="kpi-today-accepted"
        />
        <KpiCard
          Icon={Wallet}
          label="Wallet balance"
          value={`£${wallet_balance.toFixed(2)}`}
          sub={wallet_balance < 50 ? "Top up soon" : "Sufficient"}
          testid="kpi-wallet"
          warn={wallet_balance < 50}
        />
        <KpiCard
          Icon={ShieldCheck}
          label="Delivery state"
          value={delivery_paused ? "PAUSED" : "LIVE"}
          sub={delivery_hours?.enabled ? `${delivery_hours.start}–${delivery_hours.end}` : "24/7 routing"}
          testid="kpi-delivery-state"
          warn={delivery_paused}
        />
      </div>

      {/* Routing explainer */}
      <RoutingExplainer audience="client" />

      {/* Campaign list */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Your campaigns</h3>
            <span className="text-xs text-slate-500">{campaigns.length} total · {liveCamps.length} live</span>
          </div>
          {campaigns.length === 0 ? (
            <EmptyCampaigns />
          ) : (
            <ul className="divide-y divide-slate-100">
              {campaigns.map((c) => (
                <CampaignRow key={c.id} c={c} onCapSaved={refresh} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Controls grid: business hours + daily cap */}
      <div className="grid lg:grid-cols-2 gap-4">
        <DeliveryHoursCard initial={delivery_hours} onSaved={refresh} />

        <Card className="border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <Gauge className="size-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-900">Daily lead cap</h3>
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-md leading-snug">
                Hard limit on accepted leads per UTC day across all your campaigns. 0 or blank = unlimited.
              </p>
            </div>

            <div className="flex items-end gap-3">
              <label className="block flex-1">
                <span className="block text-xs font-medium text-slate-600 mb-1">Max accepted leads / day</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder="No cap"
                  value={capDraft}
                  onChange={(e) => setCapDraft(e.target.value)}
                  data-testid="daily-cap-input"
                  className="h-9"
                />
              </label>
              <Button
                size="sm"
                onClick={saveCap}
                disabled={savingCap}
                data-testid="daily-cap-save"
                className="bg-slate-900 hover:bg-slate-800"
              >
                {savingCap ? <Loader2 className="size-3.5 mr-1.5 animate-spin" /> : null}
                Save
              </Button>
            </div>

            <div className="rounded-sm border border-slate-100 bg-slate-50 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Today's usage</div>
              <div className="mt-0.5 text-sm font-semibold text-slate-900 tabular-nums">
                {totalToday.toLocaleString()} accepted
                {daily_lead_cap ? <span className="text-slate-500 font-normal"> / {daily_lead_cap.toLocaleString()} cap</span> : <span className="text-slate-500 font-normal"> · no cap set</span>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ Icon, label, value, sub, testid, warn }) {
  return (
    <Card className={`border-slate-200 ${warn ? "bg-amber-50/40" : ""}`} data-testid={testid}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500 font-medium">
          <Icon className="size-3.5" /> {label}
        </div>
        <div className="text-xl font-semibold text-slate-900 tabular-nums leading-none">{value}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </CardContent>
    </Card>
  );
}

function CampaignRow({ c, onCapSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [capDraft, setCapDraft] = React.useState(c.daily_cap ?? "");
  const [saving, setSaving] = React.useState(false);
  const last = c.last_lead_at ? new Date(c.last_lead_at) : null;
  const ago = last ? timeAgo(last) : null;

  const saveCap = async () => {
    setSaving(true);
    try {
      const cap = capDraft === "" || capDraft === null ? 0 : Math.max(0, parseInt(capDraft, 10) || 0);
      await api.patch(`/client/campaigns/${c.id}/cap`, { daily_cap: cap });
      toast.success(cap === 0 ? `Cap removed on ${c.name}.` : `Cap set to ${cap.toLocaleString()} / day on ${c.name}.`);
      setEditing(false);
      onCapSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save cap");
    } finally {
      setSaving(false);
    }
  };

  const saveCriteria = async (next) => {
    await api.patch(`/client/campaigns/${c.id}/criteria`, { criteria: next });
    onCapSaved?.();
  };

  return (
    <li className="px-5 py-4 hover:bg-slate-50/60 transition-colors" data-testid={`campaign-row-${c.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
            {c.live
              ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wide font-medium">Live</Badge>
              : <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide font-medium">Paused</Badge>}
            {c.daily_cap ? (
              <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide font-mono">
                cap {c.daily_cap.toLocaleString()}/day · {c.today_accepted}/{c.daily_cap} today
              </Badge>
            ) : null}
            {(c.criteria || []).length > 0 ? (
              <Badge variant="outline" className="border-[#0055FF]/30 bg-[#0055FF]/[0.05] text-[#0055FF] text-[10px] uppercase tracking-wide font-mono">
                {(c.criteria || []).length} criteria
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span className="font-mono uppercase tracking-wide">
              {(c.sectors && c.sectors.length > 1) ? c.sectors.join(", ") : (c.sector || "—")}
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><CalendarClock className="size-3" /> MTD {c.mtd_accepted.toLocaleString()}</span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1"><Activity className="size-3" /> Today {c.today_accepted.toLocaleString()}</span>
            {ago && <>
              <span className="text-slate-300">·</span>
              <span>last lead {ago}</span>
            </>}
          </div>
          {editing && (
            <div className="mt-3 flex items-center gap-2" data-testid={`campaign-cap-edit-${c.id}`}>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                placeholder="No cap"
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                data-testid={`campaign-cap-input-${c.id}`}
                className="h-8 w-32 text-xs"
              />
              <Button size="sm" onClick={saveCap} disabled={saving} className="h-8 px-3 text-xs bg-slate-900 hover:bg-slate-800" data-testid={`campaign-cap-save-${c.id}`}>
                {saving ? <Loader2 className="size-3 animate-spin" /> : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setCapDraft(c.daily_cap ?? ""); }} className="h-8 px-2 text-xs text-slate-500">
                Cancel
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!editing && (
            <Button variant="ghost" size="sm" className="text-xs text-slate-600 hover:text-slate-900" onClick={() => setEditing(true)} data-testid={`campaign-cap-toggle-${c.id}`}>
              {c.daily_cap ? "Edit cap" : "Set cap"}
            </Button>
          )}
          <Button asChild variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
            <Link to={`/dashboard/leads?campaign=${c.id}`} data-testid={`view-leads-${c.id}`}>
              View leads <ArrowUpRight className="size-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </div>

      {/* iter95: Per-campaign criteria — buyer self-service editor */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <CriteriaBuilder
          value={c.criteria || []}
          onSave={saveCriteria}
          testid={`camp-${c.id.slice(0, 8)}`}
          sector={c.sector || (Array.isArray(c.sectors) ? c.sectors[0] : null)}
        />
      </div>
    </li>
  );
}

function EmptyCampaigns() {
  return (
    <div className="px-5 py-12 text-center">
      <Megaphone className="size-10 text-slate-300 mx-auto" />
      <h4 className="mt-3 text-sm font-semibold text-slate-900">No campaigns yet</h4>
      <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
        Once your reservation is approved by Connexis it'll appear here as a live campaign and start receiving matching leads.
      </p>
      <Button asChild size="sm" className="mt-4 bg-slate-900 hover:bg-slate-800">
        <Link to="/buy-leads" data-testid="buy-leads-cta">Order leads</Link>
      </Button>
    </div>
  );
}

function timeAgo(d) {
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
